/**
 * Edit Message Modal Component
 * 
 * Modal for editing an existing message.
 */

import React, { useEffect, useState } from 'react';
import { Modal, Input, Form, App } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Message } from '@municipality/types';

const { TextArea } = Input;

interface EditMessageModalProps {
  open: boolean;
  message: Message | null;
  onClose: () => void;
  onConfirm: (messageId: string, newContent: string) => Promise<void>;
}

export const EditMessageModal: React.FC<EditMessageModalProps> = ({
  open,
  message,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('chat');
  const { message: antdMessage } = App.useApp();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && message) {
      form.setFieldsValue({ content: message.content });
    }
  }, [open, message, form]);

  const handleSave = async () => {
    if (!message) return;

    try {
      const values = await form.validateFields();
      const newContent = values.content.trim();

      // Check if content actually changed
      if (newContent === message.content.trim()) {
        onClose();
        return;
      }

      setIsSaving(true);
      await onConfirm(message.id, newContent);
      antdMessage.success(t('editMessage.success') || 'Message edited successfully');
      onClose();
    } catch (error) {
      console.error('Failed to edit message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to edit message';
      antdMessage.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (!message) {
    return null;
  }

  return (
    <Modal
      title={t('editMessage.title') || 'Edit message'}
      open={open}
      onOk={handleSave}
      onCancel={onClose}
      confirmLoading={isSaving}
      okText={t('editMessage.save') || 'Save'}
      cancelText={t('buttons.cancel', { ns: 'common' }) || 'Cancel'}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" name="edit_message_form">
        <Form.Item
          name="content"
          rules={[
            { required: true, message: t('editMessage.emptyError') || 'Please enter a message' },
            { max: 10000, message: t('editMessage.longError') || 'Message cannot exceed 10000 characters' },
          ]}
        >
          <TextArea
            placeholder={t('editMessage.placeholder') || 'Edit your message...'}
            rows={4}
            autoFocus
            showCount
            maxLength={10000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

